import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, CheckCircle2, XCircle } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';

export default function ContractorPortalAuth() {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Authenticating...');
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    const handleAuth = async () => {
      try {
        // Check if user is already authenticated
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          setStatus('success');
          setMessage('Authentication successful! Redirecting...');
          setTimeout(() => {
            navigate('/contractor-portal/dashboard');
          }, 1000);
          return;
        }

        // Get the invitation token from URL
        const token = searchParams.get('token');

        if (!token) {
          setStatus('error');
          setMessage('No invitation token provided. Please use the link from your invitation email.');
          return;
        }

        console.log('Processing invitation token...');

        // Verify the invitation token
        const { data, error } = await supabase.functions.invoke('verify-contractor-invitation', {
          body: { token }
        });

        if (error) {
          console.error('Token verification error:', error);
          setStatus('error');
          setMessage(error.message || 'Failed to verify invitation. Please try again or contact support.');
          return;
        }

        if (!data || !data.success) {
          setStatus('error');
          setMessage(data?.error || 'Invalid invitation token. Please request a new invitation.');
          return;
        }

      console.log('Token verified, redirecting to magic link...');

        // The backend returns a magic link - redirect to it for authentication
        if (data.magicLink) {
          setStatus('success');
          setMessage(`Welcome ${data.firstName || ''}! Redirecting to authenticate...`);
          
          // Redirect to the magic link - Supabase will handle auth and redirect back
          setTimeout(() => {
            window.location.href = data.magicLink;
          }, 500);
        } else {
          setStatus('error');
          setMessage('Authentication response invalid. Please try again or contact support.');
        }
      } catch (error: any) {
        console.error('Auth error:', error);
        setStatus('error');
        setMessage(error.message || 'Authentication failed. Please try again or contact support.');
      }
    };

    handleAuth();
  }, [navigate, searchParams]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-muted p-6">
      <div className="max-w-md w-full space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Contractor Portal
          </h1>
          <p className="text-muted-foreground">
            Secure document management and prequalification
          </p>
        </div>

        <div className="bg-card border rounded-lg p-6 shadow-lg">
          {status === 'loading' && (
            <div className="flex flex-col items-center gap-4 py-8">
              <Loader2 className="h-12 w-12 animate-spin text-primary" />
              <p className="text-sm text-muted-foreground">{message}</p>
            </div>
          )}

          {status === 'success' && (
            <Alert className="border-green-200 bg-green-50 dark:bg-green-950/20">
              <CheckCircle2 className="h-5 w-5 text-green-600" />
              <AlertDescription className="text-green-800 dark:text-green-200">
                {message}
              </AlertDescription>
            </Alert>
          )}

          {status === 'error' && (
            <div className="space-y-4">
              <Alert variant="destructive">
                <XCircle className="h-5 w-5" />
                <AlertDescription>{message}</AlertDescription>
              </Alert>
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Need help? Contact your project manager for a new invitation link.
                </p>
                <Button
                  variant="outline"
                  onClick={() => window.location.href = '/'}
                >
                  Return to Home
                </Button>
              </div>
            </div>
          )}
        </div>

        <div className="text-center text-xs text-muted-foreground">
          <p>Powered by CRM Platform</p>
          <p className="mt-1">
            For support, contact your project manager
          </p>
        </div>
      </div>
    </div>
  );
}
